import React, { useCallback, useEffect, useState } from "react";
import { subDays, parseISO } from "date-fns";
import { useExtractBalanceSheet } from "@api/fileScanning.api";
import { useCreateImportedAccountsFromBalanceSheet } from "@api/accountingIntegration.api";
import { fetchFinancialDocument } from "@lib/financialDocument";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    BalanceSheetAccountClass,
    AccountClass,
} from "@common/constants/domains/accounting/accounts";
import { FinancialDocument } from "@common/types/financialDocument";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { useScope } from "@state/scopeContext.lib";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { usePreventBodyScroll } from "@design-system/Drawer/usePreventBodyScroll";
import { BalanceSheetImporterModalContent } from "./BalanceSheetImporterModalContent";
import { MapAccountsStep } from "./MapAccountsStep";
import { useBalanceSheetRowSync } from "./useBalanceSheetRowSync";
import styles from "./BalanceSheetImporter.module.scss";
import {
    AccountProxyObject,
    BalanceSheetImportStep,
    BalanceSheetRowAction,
} from "./types";
import { validateBalanceSheetAccounts, ValidationResult } from "./validation";

const BALANCE_SHEET_CLASS_TO_ACCOUNT_CLASS: Record<
    BalanceSheetAccountClass,
    AccountClass
> = {
    [BalanceSheetAccountClass.assets]: AccountClass.assets,
    [BalanceSheetAccountClass.liabilities]: AccountClass.liabilities,
    [BalanceSheetAccountClass.equity]: AccountClass.equity,
};

interface Props {
    show: boolean;
    onHide: () => void;
    availableClassifications?: BalanceSheetAccountClass[];
}

const DEFAULT_NUMBER_OF_LINES = 10;

function buildEmptyLines(
    size: number = DEFAULT_NUMBER_OF_LINES,
): AccountProxyObject[] {
    return new Array(size).fill(null).map(() => ({
        id: Math.random().toString(),
        name: "",
        accountClass: null,
        balance: null,
        accountType: null,
        accountSubtype: null,
        mappedKickAccountId: null,
        action: BalanceSheetRowAction.CreateAccount,
        isActionAccepted: false,
    }));
}

export const BalanceSheetAccountMappingModal: React.FC<Props> = React.memo(
    ({ show, onHide, availableClassifications }) => {
        const { toast } = useToaster();
        const { selectedEntity, setFilters } = useAccountingTabContext();
        const { workspaceId } = useScope();
        const [accounts, setAccounts] =
            useState<AccountProxyObject[]>(buildEmptyLines());
        const [validationResult, setValidationResult] =
            useState<ValidationResult>({ errors: {}, display: {} });
        const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
        const [step, setStep] = useState<BalanceSheetImportStep>(
            BalanceSheetImportStep.UploadDocument,
        );
        const [extractedDate, setExtractedDate] = useState<string | null>(null);
        const [uploadedDocumentId, setUploadedDocumentId] = useState<
            number | null
        >(null);

        usePreventBodyScroll(show);

        const extractBalanceSheet = useExtractBalanceSheet();
        const createImportedAccounts =
            useCreateImportedAccountsFromBalanceSheet(
                workspaceId ?? "",
                selectedEntity?.id ?? 0,
            );
        const { afterChange, syncDate, initializeSnapshot } =
            useBalanceSheetRowSync({
                financialDocumentId: uploadedDocumentId,
                onSyncError: (error) =>
                    toast(`Failed to save balance sheet: ${error.message}`),
            });
        const bookkeepingStartDate = selectedEntity?.bookkeepingStartDate;

        const validateAccounts = useCallback(
            (): ValidationResult =>
                validateBalanceSheetAccounts({
                    accounts,
                    step,
                    extractedDate,
                    bookkeepingStartDate,
                }),
            [accounts, step, extractedDate, bookkeepingStartDate],
        );

        useEffect(() => {
            setValidationResult(validateAccounts());
        }, [accounts, validateAccounts, extractedDate]);

        const handleSave = useCallback(() => {
            toast("Accounts mapped");

            if (bookkeepingStartDate) {
                const dayBeforeStartDate = subDays(
                    parseISO(bookkeepingStartDate),
                    1,
                );
                setFilters((current) => ({
                    ...current,
                    startDate: dayBeforeStartDate,
                    endDate: dayBeforeStartDate,
                    datePreset: undefined,
                }));
            }

            setAccounts(buildEmptyLines());
            setValidationResult({ errors: {}, display: {} });
            setHasAttemptedSubmit(false);
            setStep(BalanceSheetImportStep.UploadDocument);
            setExtractedDate(null);
            setUploadedDocumentId(null);
            onHide();
        }, [toast, onHide, bookkeepingStartDate, setFilters]);

        const handleHide = useCallback(() => {
            setAccounts(buildEmptyLines());
            setValidationResult({ errors: {}, display: {} });
            setHasAttemptedSubmit(false);
            setStep(BalanceSheetImportStep.UploadDocument);
            setExtractedDate(null);
            setUploadedDocumentId(null);
            onHide();
        }, [onHide]);

        const handleStepChange = useCallback(
            async (newStep: BalanceSheetImportStep) => {
                if (
                    newStep === BalanceSheetImportStep.MapAccounts &&
                    step === BalanceSheetImportStep.EnterBalances
                ) {
                    setHasAttemptedSubmit(true);
                    const result = validateAccounts();
                    setValidationResult(result);

                    if (Object.keys(result.errors).length > 0) {
                        return;
                    }

                    if (!selectedEntity || !workspaceId) {
                        toast("Select an entity first");
                        return;
                    }

                    const isAccountWithClassification = (
                        account: AccountProxyObject,
                    ): account is AccountProxyObject & {
                        accountClass: AccountClass;
                    } => !!account.name?.trim() && !!account.accountClass;

                    const filledAccounts = accounts.filter(
                        isAccountWithClassification,
                    );

                    if (filledAccounts.length === 0) {
                        toast("Enter at least one account name");
                        return;
                    }

                    if (uploadedDocumentId == null) {
                        toast("Upload a balance sheet document first");
                        return;
                    }

                    try {
                        await createImportedAccounts.mutateAsync({
                            financialDocumentId: uploadedDocumentId,
                            accounts: filledAccounts.map((a) => ({
                                name: a.name,
                                classification: a.accountClass,
                                balance: a.balance,
                            })),
                        });
                        setAccounts(filledAccounts);
                        setStep(newStep);
                    } catch (error) {
                        toast(
                            `Failed to create account mapping: ${error instanceof Error ? error.message : "Please try again."}`,
                        );
                    }

                    return;
                }

                if (newStep === BalanceSheetImportStep.EnterBalances) {
                    setHasAttemptedSubmit(false);
                }

                setStep(newStep);
            },
            [
                step,
                validateAccounts,
                selectedEntity,
                workspaceId,
                accounts,
                createImportedAccounts,
                uploadedDocumentId,
                toast,
            ],
        );

        const handleAccountsChange = useCallback(
            (newAccounts: AccountProxyObject[]) => {
                setAccounts(newAccounts);
                afterChange(newAccounts);
            },
            [afterChange],
        );

        const handleDateChange = useCallback(
            (date: string | null) => {
                setExtractedDate(date);
                syncDate(date);
            },
            [syncDate],
        );

        const loadExtractionIntoState = useCallback(
            (extraction: {
                accounts: Array<{
                    name: string;
                    balance: number | null;
                    classification: BalanceSheetAccountClass | null;
                }>;
                date: string | null;
            }) => {
                const newAccounts: AccountProxyObject[] =
                    extraction.accounts.map((account) => ({
                        id: Math.random().toString(),
                        name: account.name,
                        accountClass: account.classification
                            ? (BALANCE_SHEET_CLASS_TO_ACCOUNT_CLASS[
                                  account.classification
                              ] ?? null)
                            : null,
                        balance: account.balance,
                        accountType: null,
                        accountSubtype: null,
                        mappedKickAccountId: null,
                        action: BalanceSheetRowAction.CreateAccount,
                        isActionAccepted: false,
                    }));

                setExtractedDate(extraction.date);

                const accountsWithEmptyLines = [
                    ...newAccounts,
                    ...buildEmptyLines(),
                ];

                initializeSnapshot(accountsWithEmptyLines, extraction.date);
                setAccounts(accountsWithEmptyLines);
                setStep(BalanceSheetImportStep.EnterBalances);
            },
            [initializeSnapshot],
        );

        const processExtractedBalanceSheet = useCallback(
            async (documentId: number) => {
                if (!selectedEntity) {
                    toast("Select an entity first");
                    return;
                }

                setUploadedDocumentId(documentId);

                try {
                    const result = await extractBalanceSheet.mutateAsync({
                        entityId: selectedEntity.id,
                        documentId,
                    });

                    loadExtractionIntoState({
                        accounts: result.accounts,
                        date: result.date,
                    });

                    if (result.warnings.length > 0) {
                        toast(
                            `Extracted ${pluralizeWithCount("account", result.accounts.length)}. ${result.warnings[0]}`,
                        );
                    }
                } catch (uploadError) {
                    toast(
                        `Failed to upload file: ${uploadError instanceof Error ? uploadError.message : "Please try again."}`,
                    );
                }
            },
            [
                selectedEntity,
                extractBalanceSheet,
                toast,
                loadExtractionIntoState,
            ],
        );

        const handleDocumentSelect = useCallback(
            async (documentId: number) => {
                setUploadedDocumentId(documentId);

                try {
                    const document = await fetchFinancialDocument(documentId);
                    const balanceSheet = document.balanceSheetExtraction;
                    if (balanceSheet?.rows && balanceSheet.rows.length > 0) {
                        loadExtractionIntoState({
                            accounts: balanceSheet.rows.map((row) => ({
                                name: row.name,
                                balance: row.balance,
                                classification: row.classification,
                            })),
                            date: balanceSheet.date ?? null,
                        });
                        return;
                    }
                } catch {
                    // Fetch failed — fall through to re-extract from scratch
                }

                await processExtractedBalanceSheet(documentId);
            },
            [processExtractedBalanceSheet, loadExtractionIntoState],
        );

        const handleFileUploadCompleted = useCallback(
            async (uploadedDocuments: FinancialDocument[]) => {
                const financialDocument = uploadedDocuments[0];
                if (financialDocument) {
                    await processExtractedBalanceSheet(financialDocument.id);
                }
            },
            [processExtractedBalanceSheet],
        );

        const renderMapAccountsStep = () => {
            if (!selectedEntity) {
                return null;
            }
            return (
                <>
                    <StandardModalHeader>
                        Import & map accounts
                    </StandardModalHeader>
                    <StandardModalBody className={styles.modalBody}>
                        <div className={styles.contentContainer}>
                            <div className={styles.rightPanel}>
                                <MapAccountsStep
                                    entity={selectedEntity}
                                    onSave={handleSave}
                                />
                            </div>
                        </div>
                    </StandardModalBody>
                </>
            );
        };

        const renderModalContent = () => {
            if (!bookkeepingStartDate) {
                return (
                    <>
                        <StandardModalHeader>
                            Import balance sheet
                        </StandardModalHeader>
                        <StandardModalBody className="p-4 text-center">
                            <p className="text-muted">
                                Please set a bookkeeping start date for this
                                entity before importing opening balances.
                            </p>
                        </StandardModalBody>
                    </>
                );
            }

            if (step === BalanceSheetImportStep.MapAccounts) {
                return renderMapAccountsStep();
            }

            return (
                <FinancialDocumentUploadProvider
                    onCompleted={handleFileUploadCompleted}
                >
                    <BalanceSheetImporterModalContent
                        loading={false}
                        availableClassifications={availableClassifications}
                        accounts={accounts}
                        onAccountsChange={handleAccountsChange}
                        onSave={handleSave}
                        validationResult={validationResult}
                        hasAttemptedSubmit={hasAttemptedSubmit}
                        onDocumentSelect={handleDocumentSelect}
                        isExtracting={extractBalanceSheet.isPending}
                        isMapping={createImportedAccounts.isPending}
                        step={step}
                        onStepChange={handleStepChange}
                        bookkeepingStartDate={bookkeepingStartDate}
                        extractedDate={extractedDate}
                        onExtractedDateChange={handleDateChange}
                        uploadedDocumentId={uploadedDocumentId}
                    />
                </FinancialDocumentUploadProvider>
            );
        };

        return (
            <StandardModal
                fullscreen
                onHide={handleHide}
                show={show}
                disableAnimation
            >
                {renderModalContent()}
            </StandardModal>
        );
    },
);
